import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  User, Briefcase, MapPin, GraduationCap, Star,
  Building, Globe, ChevronRight, ChevronLeft, Check,
  Loader, Plus, X, Target, Car, Trash2, BookOpen, Zap,
} from 'lucide-react';
import { useApp } from '../context/AppContext';
import { updateProfile } from '../services/api';
import LocationInput from '../components/LocationInput';
import BirthDateInput from '../components/BirthDateInput';
import { FilterFields } from '../components/FilterDrawer';
import { filtersToParams, paramsToFilters } from '../hooks/useJobFilters';
import {
  SCHOOL_STATUS_LABELS, TRAINING_STATUS_LABELS, EXPERIENCE_TYPE_LABELS,
  LANGUAGE_LEVEL_LABELS, DRIVERS_LICENSE_LABELS, LANGUAGE_SUGGESTIONS,
  SKILL_GROUPS, PRESET_SKILLS,
} from '../constants/profileOptions';
import './SetupPage.css';

const INDUSTRIES = [
  'Einzelhandel', 'Gastronomie & Hotellerie', 'Handwerk & Bau',
  'IT & Technologie', 'Gesundheit & Pflege', 'Bildung & Soziales',
  'Logistik & Transport', 'Produktion & Fertigung', 'Büro & Verwaltung',
  'Marketing & Kommunikation', 'Sonstiges',
];

// Schritt 5 "Fertig" ist die WelcomeScreen-Seite (/welcome) nach dem Speichern
const YOUTH_STEPS = [
  { key: 'basis', title: 'Basisdaten', subtitle: 'Wer bist du und wo wohnst du?', Icon: User },
  { key: 'about', title: 'Erzähl uns von dir', subtitle: 'Das sieht der Arbeitgeber, wenn du dich bewirbst', Icon: GraduationCap },
  { key: 'bio', title: 'Über mich', subtitle: 'Ein paar Sätze in deinen eigenen Worten', Icon: Star },
  { key: 'wishes', title: 'Wünsche', subtitle: 'Kannst du jederzeit ändern', Icon: Target },
];
const DONE_STEP = { key: 'done', title: 'Fertig' };

// Profilseite öffnet dieselben Screens über /profile/edit/:section
const EDITABLE_SECTIONS = ['about', 'bio', 'wishes'];

const EMPLOYER_STEPS = [
  { key: 'company', title: 'Firmendaten', subtitle: 'Stell dein Unternehmen vor', Icon: Building },
  { key: 'companyBio', title: 'Firmenvorstellung', subtitle: 'Was macht euch aus?', Icon: Star },
];

const BIO_EXAMPLE =
  'z.B. Ich bin 16 und gehe in die 10. Klasse der Realschule plus. In meiner Freizeit spiele ich '
  + 'Handball im Verein und helfe bei der Jugendfeuerwehr. Ich bin zuverlässig, arbeite gern im Team '
  + 'und möchte in den Ferien erste Erfahrungen im Einzelhandel sammeln.';

const MAX_YEAR = new Date().getFullYear() + 1;
const emptyExperience = () => ({ type: 'nebenjob', organization: '', period: '', description: '' });
const hasContent = (e) => e.organization.trim() || e.period.trim() || e.description.trim();
const parseGrade = (g) => Number(String(g).replace(',', '.'));
const inRange = (v, min, max) => v === '' || (Number.isInteger(Number(v)) && v >= min && v <= max);

export default function SetupPage() {
  const { user, sessionChecked } = useApp();
  const navigate = useNavigate();
  const { section } = useParams();

  useEffect(() => {
    if (sessionChecked && !user) navigate('/', { replace: true });
  }, [sessionChecked, user, navigate]);

  // Formular erst mit vollständig geladenem Profil aufbauen – sonst startet
  // "Bearbeiten" leer und würde die gespeicherten Angaben überschreiben
  const ready = user && (!section || 'profile_complete' in user);
  return ready ? <SetupForm user={user} /> : null;
}

function SetupForm({ user }) {
  const { refreshUser } = useApp();
  const navigate = useNavigate();
  const { section } = useParams();

  const isEmployer = user.user_type === 'employer';
  const isEdit = !!section;
  const steps = isEdit
    ? YOUTH_STEPS.filter(s => s.key === section)
    : isEmployer ? EMPLOYER_STEPS : YOUTH_STEPS;
  const indicator = isEmployer ? steps : [...steps, DONE_STEP];
  const nameParts = (user.name || '').split(' ');

  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [skillInput, setSkillInput] = useState('');
  // "Später ausfüllen": "Erzähl uns von dir" und "Über mich" werden nicht
  // gespeichert, es geht direkt zu den Wünschen
  const [skippedAbout, setSkippedAbout] = useState(false);

  const [youthForm, setYouthForm] = useState(() => ({
    firstName: user.first_name || nameParts[0] || '',
    lastName: user.last_name || nameParts.slice(1).join(' ') || '',
    birthDate: '',
    location: '',
    locationId: null,
    schoolStatus: user.school_status || '',
    schoolName: user.school_name || '',
    schoolClass: user.school_class ?? '',
    schoolGraduationYear: user.school_graduation_year ?? '',
    schoolGrade: user.school_grade != null ? String(user.school_grade).replace('.', ',') : '',
    trainingStatus: user.training_status || 'keins',
    trainingField: user.training_field || '',
    // Server speichert leere Angaben als null, die Eingabefelder brauchen ''
    experience: (user.experience || []).map(e => ({
      ...emptyExperience(), ...e, period: e.period || '', description: e.description || '',
    })),
    skills: user.skills || [],
    languages: user.languages || [],
    driversLicense: user.drivers_license || '',
    bio: user.bio || '',
  }));
  // Wünsche nutzen den Filter-State von useJobFilters, damit FilterFields passt
  const [wishes, setWishes] = useState(() => paramsToFilters(user.preferences));

  const [employerForm, setEmployerForm] = useState({
    companyName: user.company_name || '',
    industry: '',
    address: '',
    website: '',
    bio: '',
  });

  const invalidSection = isEdit && (isEmployer || !EDITABLE_SECTIONS.includes(section));
  useEffect(() => {
    if (invalidSection) navigate('/profile', { replace: true });
  }, [invalidSection, navigate]);
  if (invalidSection) return null;

  const updateYouth = (field, value) => setYouthForm(prev => ({ ...prev, [field]: value }));
  const updateEmployer = (field, value) => setEmployerForm(prev => ({ ...prev, [field]: value }));

  const toggleSkill = (skill) => setYouthForm(prev => ({
    ...prev,
    skills: prev.skills.includes(skill) ? prev.skills.filter(s => s !== skill) : [...prev.skills, skill],
  }));

  const updateListItem = (field, index, patch) => setYouthForm(prev => ({
    ...prev,
    [field]: prev[field].map((item, i) => (i === index ? { ...item, ...patch } : item)),
  }));
  const removeListItem = (field, index) => setYouthForm(prev => ({
    ...prev,
    [field]: prev[field].filter((_, i) => i !== index),
  }));
  const addListItem = (field, item) => setYouthForm(prev => ({ ...prev, [field]: [...prev[field], item] }));

  const addSkill = () => {
    const s = skillInput.trim();
    if (s && !youthForm.skills.includes(s)) {
      updateYouth('skills', [...youthForm.skills, s]);
      setSkillInput('');
    }
  };

  const f = youthForm;
  const isPupil = f.schoolStatus === 'schueler';
  const customSkills = f.skills.filter(s => !PRESET_SKILLS.includes(s));

  // Gleiche Regeln wie serverseitig (profileController), hier nur für die
  // Rückmeldung direkt am Schritt statt erst beim Speichern
  const aboutProblem = () => {
    if (!f.schoolStatus) return 'Wähle aus, ob du noch zur Schule gehst oder welchen Abschluss du hast.';
    if (isPupil && !inRange(f.schoolClass, 1, 13)) return 'Die Klasse muss zwischen 1 und 13 liegen.';
    if (!isPupil && !inRange(f.schoolGraduationYear, 1950, MAX_YEAR)) return 'Bitte ein gültiges Abschlussjahr eingeben.';
    const grade = parseGrade(f.schoolGrade);
    if (f.schoolGrade !== '' && !(grade >= 1 && grade <= 6)) return 'Die Note muss zwischen 1,0 und 6,0 liegen.';
    if (f.experience.some(e => hasContent(e) && !e.organization.trim())) {
      return 'Bei einer Erfahrung fehlt noch die Firma oder Organisation.';
    }
    if (f.languages.some(l => l.language.trim() && !l.level)) return 'Wähle bei jeder Sprache ein Niveau aus.';
    return null;
  };

  const stepKey = steps[step].key;
  const stepProblem = stepKey === 'about' ? aboutProblem() : null;

  const canAdvance = () => {
    if (stepKey === 'basis') return f.firstName.trim() && f.lastName.trim() && f.location && f.birthDate !== null;
    if (stepKey === 'about') return !stepProblem;
    if (stepKey === 'company') return employerForm.companyName.trim() && employerForm.industry;
    return true;
  };

  // Je Abschnitt nur die eigenen Felder – das Backend schreibt nur, was mitkommt
  const sectionPayload = {
    basis: () => ({
      first_name: f.firstName,
      last_name: f.lastName,
      birth_date: f.birthDate,
      location: f.location,
      location_id: f.locationId,
    }),
    about: () => ({
      school_status: f.schoolStatus,
      school_name: f.schoolName,
      school_class: isPupil ? f.schoolClass : '',
      school_graduation_year: isPupil ? '' : f.schoolGraduationYear,
      school_grade: f.schoolGrade,
      training_status: f.trainingStatus,
      training_field: f.trainingStatus === 'keins' ? '' : f.trainingField,
      experience: f.experience.filter(hasContent),
      skills: f.skills,
      languages: f.languages.filter(l => l.language.trim()),
      drivers_license: f.driversLicense,
    }),
    bio: () => ({ bio: f.bio }),
    wishes: () => ({ preferences: filtersToParams(wishes) }),
  };

  const save = async (sections) => {
    setLoading(true);
    setError(null);
    try {
      const payload = isEmployer
        ? {
            company_name: employerForm.companyName,
            industry: employerForm.industry,
            address: employerForm.address,
            website: employerForm.website,
            bio: employerForm.bio,
          }
        : Object.assign({}, ...sections.map(key => sectionPayload[key]()));
      await updateProfile(payload);
      await refreshUser();
      navigate(isEdit ? '/profile' : '/welcome', { replace: true });
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.message || 'Profil konnte nicht gespeichert werden.');
      setLoading(false);
    }
  };

  const filledSections = skippedAbout ? ['basis'] : ['basis', 'about', 'bio'];
  const indexOf = (key) => steps.findIndex(s => s.key === key);
  const isLastStep = step === steps.length - 1;
  const progress = ((step + 1) / indicator.length) * 100;
  const { Icon: StepIcon } = steps[step];

  const chip = (active, onClick, label, key = label) => (
    <button
      key={key}
      type="button"
      className={`setup-chip${active ? ' is-active' : ''}`}
      onClick={onClick}
      aria-pressed={active}
    >
      {label}
    </button>
  );

  return (
    <div className="setup-page">
      <div className="setup-page__bg">
        <div className="setup-page__blob setup-page__blob--1" />
        <div className="setup-page__blob setup-page__blob--2" />
      </div>

      <div className="setup-page__inner">
        {/* Logo & heading */}
        <div className="setup-header">
          <div className="setup-logo">
            <Briefcase size={22} />
          </div>
          <h1>{isEdit ? 'Profil bearbeiten' : 'Profil einrichten'}</h1>
          {!isEdit && <p>Schritt {step + 1} von {indicator.length}</p>}
        </div>

        {!isEdit && (
          <>
            {/* Step indicators */}
            <div className="setup-steps">
              {indicator.map((s, i) => (
                <React.Fragment key={s.key}>
                  <div className={`setup-step ${i < step ? 'is-done' : ''} ${i === step ? 'is-active' : ''}`}>
                    <div className="setup-step__circle">
                      {i < step ? <Check size={13} strokeWidth={3} /> : <span>{i + 1}</span>}
                    </div>
                    <span className="setup-step__label">{s.title}</span>
                  </div>
                  {i < indicator.length - 1 && (
                    <div className={`setup-step__line ${i < step ? 'is-done' : ''}`} />
                  )}
                </React.Fragment>
              ))}
            </div>

            {/* Progress bar */}
            <div className="setup-progress">
              <div className="setup-progress__fill" style={{ width: `${progress}%` }} />
            </div>
          </>
        )}

        {/* Card */}
        <div className="setup-card">
          <div className="setup-card__heading">
            <div className="setup-card__icon">
              <StepIcon size={20} />
            </div>
            <div>
              <h2>{steps[step].title}</h2>
              <p>{steps[step].subtitle}</p>
            </div>
          </div>

          {error && <div className="setup-error">{error}</div>}

          {/* ── Youth: Basisdaten ── */}
          {stepKey === 'basis' && (
            <div className="setup-fields">
              <div className="setup-row">
                <div className="setup-field">
                  <label>Vorname *</label>
                  <input
                    type="text"
                    value={f.firstName}
                    onChange={e => updateYouth('firstName', e.target.value)}
                    placeholder="Max"
                  />
                </div>
                <div className="setup-field">
                  <label>Nachname *</label>
                  <input
                    type="text"
                    value={f.lastName}
                    onChange={e => updateYouth('lastName', e.target.value)}
                    placeholder="Mustermann"
                  />
                </div>
              </div>
              <div className="setup-field">
                <label>Geburtsdatum</label>
                <BirthDateInput value={f.birthDate} onChange={v => updateYouth('birthDate', v)} />
              </div>
              <div className="setup-field">
                <label><MapPin size={13} /> Wohnort *</label>
                <LocationInput
                  value={f.location}
                  onChange={(text, loc) =>
                    setYouthForm(prev => ({ ...prev, location: text, locationId: loc?.id ?? null }))
                  }
                  placeholder="Ort suchen, z.B. Kaiserslautern"
                />
              </div>
            </div>
          )}

          {/* ── Youth: Erzähl uns von dir ── */}
          {stepKey === 'about' && (
            <div className="setup-fields">
              <h3 className="setup-section-title"><GraduationCap size={15} /> Schule</h3>
              <div className="setup-field">
                <label>Schule oder höchster Abschluss *</label>
                <select value={f.schoolStatus} onChange={e => updateYouth('schoolStatus', e.target.value)}>
                  <option value="">Bitte auswählen...</option>
                  {Object.entries(SCHOOL_STATUS_LABELS).map(([slug, label]) => (
                    <option key={slug} value={slug}>{label}</option>
                  ))}
                </select>
              </div>
              {f.schoolStatus && (
                <>
                  <div className="setup-row">
                    <div className="setup-field">
                      <label>{isPupil ? 'Schule' : 'Schule (optional)'}</label>
                      <input
                        type="text"
                        value={f.schoolName}
                        onChange={e => updateYouth('schoolName', e.target.value)}
                        placeholder="z.B. IGS Kaiserslautern"
                        maxLength={150}
                      />
                    </div>
                    {isPupil ? (
                      <div className="setup-field">
                        <label>Klasse</label>
                        <input
                          type="number"
                          inputMode="numeric"
                          min={1}
                          max={13}
                          value={f.schoolClass}
                          onChange={e => updateYouth('schoolClass', e.target.value)}
                          placeholder="z.B. 10"
                        />
                      </div>
                    ) : (
                      <div className="setup-field">
                        <label>Abschlussjahr</label>
                        <input
                          type="number"
                          inputMode="numeric"
                          min={1950}
                          max={MAX_YEAR}
                          value={f.schoolGraduationYear}
                          onChange={e => updateYouth('schoolGraduationYear', e.target.value)}
                          placeholder="z.B. 2025"
                        />
                      </div>
                    )}
                  </div>
                  <div className="setup-field setup-field--narrow">
                    <label>Note (optional)</label>
                    <input
                      type="text"
                      inputMode="decimal"
                      maxLength={3}
                      value={f.schoolGrade}
                      onChange={e => updateYouth('schoolGrade', e.target.value.replace(/[^\d,.]/g, ''))}
                      placeholder="z.B. 2,3"
                    />
                    <p className="setup-hint">
                      {isPupil ? 'Dein Schnitt im letzten Zeugnis.' : 'Deine Abschlussnote.'} Freiwillig.
                    </p>
                  </div>
                </>
              )}

              <h3 className="setup-section-title"><BookOpen size={15} /> Ausbildung / Studium</h3>
              <div className="setup-chips">
                {Object.entries(TRAINING_STATUS_LABELS).map(([slug, label]) =>
                  chip(f.trainingStatus === slug, () => updateYouth('trainingStatus', slug), label, slug))}
              </div>
              {f.trainingStatus !== 'keins' && (
                <div className="setup-field">
                  <label>Fachrichtung</label>
                  <input
                    type="text"
                    value={f.trainingField}
                    onChange={e => updateYouth('trainingField', e.target.value)}
                    placeholder="z.B. Kaufmann/-frau im Einzelhandel"
                    maxLength={150}
                  />
                </div>
              )}

              <h3 className="setup-section-title"><Briefcase size={15} /> Erfahrung</h3>
              <p className="setup-hint setup-hint--block">
                Nebenjob, Praktikum oder Ehrenamt – Verein, Feuerwehr, Trainer, alles zählt.
              </p>
              {f.experience.map((e, i) => (
                <div key={i} className="setup-experience">
                  <div className="setup-experience__head">
                    <div className="setup-chips">
                      {Object.entries(EXPERIENCE_TYPE_LABELS).map(([slug, label]) =>
                        chip(e.type === slug, () => updateListItem('experience', i, { type: slug }), label, slug))}
                    </div>
                    <button
                      type="button"
                      className="setup-icon-btn"
                      onClick={() => removeListItem('experience', i)}
                      aria-label="Erfahrung entfernen"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                  <div className="setup-row">
                    <div className="setup-field">
                      <label>Firma / Organisation *</label>
                      <input
                        type="text"
                        value={e.organization}
                        onChange={ev => updateListItem('experience', i, { organization: ev.target.value })}
                        placeholder="z.B. Bäckerei Müller, TuS Landstuhl"
                        maxLength={150}
                      />
                    </div>
                    <div className="setup-field">
                      <label>Zeitraum</label>
                      <input
                        type="text"
                        value={e.period}
                        onChange={ev => updateListItem('experience', i, { period: ev.target.value })}
                        placeholder="z.B. Sommer 2025, seit 2023"
                        maxLength={60}
                      />
                    </div>
                  </div>
                  <div className="setup-field">
                    <label>Was hast du gemacht? (ein Satz)</label>
                    <input
                      type="text"
                      value={e.description}
                      onChange={ev => updateListItem('experience', i, { description: ev.target.value })}
                      placeholder="z.B. Brötchen verkauft und an der Kasse gearbeitet"
                      maxLength={300}
                    />
                  </div>
                </div>
              ))}
              <button type="button" className="setup-add-btn" onClick={() => addListItem('experience', emptyExperience())}>
                <Plus size={16} /> Erfahrung hinzufügen
              </button>

              <h3 className="setup-section-title"><Zap size={15} /> Skills</h3>
              {SKILL_GROUPS.map(group => (
                <div key={group.title} className="setup-field">
                  <label>{group.title}</label>
                  <div className="setup-chips">
                    {group.skills.map(skill => chip(f.skills.includes(skill), () => toggleSkill(skill), skill))}
                  </div>
                </div>
              ))}
              <div className="setup-field">
                <label>Eigene Skills</label>
                {customSkills.length > 0 && (
                  <div className="setup-skills">
                    {customSkills.map(skill => (
                      <span key={skill} className="setup-skill-tag">
                        {skill}
                        <button type="button" onClick={() => toggleSkill(skill)} aria-label={`${skill} entfernen`}>
                          <X size={11} />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                <div className="setup-skill-input">
                  <input
                    type="text"
                    value={skillInput}
                    onChange={e => setSkillInput(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addSkill(); } }}
                    placeholder="z.B. Gitarre, Schach, Photoshop..."
                    maxLength={60}
                  />
                  <button type="button" className="setup-skill-add-btn" onClick={addSkill} aria-label="Skill hinzufügen">
                    <Plus size={17} />
                  </button>
                </div>
              </div>

              <h3 className="setup-section-title"><Globe size={15} /> Sprachen</h3>
              <div className="setup-field">
                {f.languages.map((l, i) => (
                  <div key={i} className="setup-language">
                    <input
                      type="text"
                      list="setup-language-list"
                      value={l.language}
                      onChange={e => updateListItem('languages', i, { language: e.target.value })}
                      placeholder="Sprache"
                      aria-label="Sprache"
                      maxLength={50}
                    />
                    <select
                      value={l.level}
                      onChange={e => updateListItem('languages', i, { level: e.target.value })}
                      aria-label="Niveau"
                    >
                      <option value="">Niveau...</option>
                      {Object.entries(LANGUAGE_LEVEL_LABELS).map(([slug, label]) => (
                        <option key={slug} value={slug}>{label}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      className="setup-icon-btn"
                      onClick={() => removeListItem('languages', i)}
                      aria-label="Sprache entfernen"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
                <datalist id="setup-language-list">
                  {LANGUAGE_SUGGESTIONS.map(lang => <option key={lang} value={lang} />)}
                </datalist>
              </div>
              <button type="button" className="setup-add-btn" onClick={() => addListItem('languages', { language: '', level: '' })}>
                <Plus size={16} /> Sprache hinzufügen
              </button>

              <h3 className="setup-section-title"><Car size={15} /> Führerschein</h3>
              <div className="setup-chips">
                {Object.entries(DRIVERS_LICENSE_LABELS).map(([slug, label]) =>
                  chip(f.driversLicense === slug,
                    () => updateYouth('driversLicense', f.driversLicense === slug ? '' : slug), label, slug))}
              </div>
            </div>
          )}

          {/* ── Youth: Über mich ── */}
          {stepKey === 'bio' && (
            <div className="setup-fields">
              <div className="setup-field">
                <label>Kurze Vorstellung</label>
                <textarea
                  value={f.bio}
                  onChange={e => updateYouth('bio', e.target.value)}
                  placeholder={BIO_EXAMPLE}
                  rows={7}
                  maxLength={500}
                />
                <p className="setup-hint">
                  Wer bist du, was machst du gern, was suchst du? Das liest der Arbeitgeber zusammen mit
                  „Erzähl uns von dir“. {f.bio.length} / 500 Zeichen
                </p>
              </div>
            </div>
          )}

          {/* ── Youth: Wünsche ── */}
          {stepKey === 'wishes' && (
            <div className="setup-fields">
              <p className="setup-wishes-note">
                Kannst du jederzeit ändern. Passende Jobs zeigen wir dir weiter oben mit „Passt zu dir“ –
                ausgeblendet wird dadurch nichts.
              </p>
              <div>
                <FilterFields draft={wishes} set={patch => setWishes(prev => ({ ...prev, ...patch }))} />
              </div>
            </div>
          )}

          {/* ── Employer: Firmendaten ── */}
          {stepKey === 'company' && (
            <div className="setup-fields">
              <div className="setup-field">
                <label><Building size={13} /> Firmenname *</label>
                <input
                  type="text"
                  value={employerForm.companyName}
                  onChange={e => updateEmployer('companyName', e.target.value)}
                  placeholder="Musterfirma GmbH"
                />
              </div>
              <div className="setup-field">
                <label>Branche *</label>
                <select
                  value={employerForm.industry}
                  onChange={e => updateEmployer('industry', e.target.value)}
                >
                  <option value="">Branche auswählen...</option>
                  {INDUSTRIES.map(ind => (
                    <option key={ind} value={ind}>{ind}</option>
                  ))}
                </select>
              </div>
              <div className="setup-field">
                <label><MapPin size={13} /> Adresse</label>
                <input
                  type="text"
                  value={employerForm.address}
                  onChange={e => updateEmployer('address', e.target.value)}
                  placeholder="Musterstraße 1, 67655 Kaiserslautern"
                />
              </div>
              <div className="setup-field">
                <label><Globe size={13} /> Website</label>
                <input
                  type="url"
                  value={employerForm.website}
                  onChange={e => updateEmployer('website', e.target.value)}
                  placeholder="https://www.meinefirma.de"
                />
              </div>
            </div>
          )}

          {/* ── Employer: Firmenvorstellung ── */}
          {stepKey === 'companyBio' && (
            <div className="setup-fields">
              <div className="setup-field">
                <label>Firmenbeschreibung</label>
                <textarea
                  value={employerForm.bio}
                  onChange={e => updateEmployer('bio', e.target.value)}
                  placeholder="Beschreibe dein Unternehmen, was ihr macht und warum Jugendliche bei euch arbeiten sollten..."
                  rows={6}
                  maxLength={1000}
                />
                <p className="setup-hint">{employerForm.bio.length} / 1000 Zeichen</p>
              </div>
            </div>
          )}

          {stepProblem && <p className="setup-hint setup-hint--error">{stepProblem}</p>}

          {/* Navigation */}
          <div className="setup-nav">
            {isEdit ? (
              <button className="setup-btn-back" onClick={() => navigate('/profile')} disabled={loading}>
                <X size={17} /> Abbrechen
              </button>
            ) : step > 0 ? (
              <button
                className="setup-btn-back"
                onClick={() => setStep(s => (skippedAbout && stepKey === 'wishes' ? indexOf('about') : s - 1))}
                disabled={loading}
              >
                <ChevronLeft size={17} /> Zurück
              </button>
            ) : <div />}
            {isEdit || isLastStep ? (
              <button
                className="setup-btn-primary"
                onClick={() => save(isEdit ? [section] : [...filledSections, 'wishes'])}
                disabled={loading || !canAdvance()}
              >
                {loading
                  ? <><Loader size={16} className="spin" /> Speichern...</>
                  : <><Check size={16} /> {isEdit ? 'Speichern' : 'Fertig'}</>}
              </button>
            ) : (
              <button
                className="setup-btn-primary"
                onClick={() => {
                  if (stepKey === 'about') setSkippedAbout(false);
                  setStep(s => s + 1);
                }}
                disabled={!canAdvance()}
              >
                Weiter <ChevronRight size={17} />
              </button>
            )}
          </div>

          {/* Weiter ohne Profilangaben – das Profil gilt dann als unvollständig */}
          {!isEdit && stepKey === 'about' && (
            <button
              className="setup-later-btn"
              onClick={() => { setSkippedAbout(true); setStep(indexOf('wishes')); }}
            >
              Später ausfüllen
            </button>
          )}
          {!isEdit && stepKey === 'wishes' && (
            <button className="setup-later-btn" onClick={() => save(filledSections)} disabled={loading}>
              Überspringen
            </button>
          )}

          {isEmployer && (
            <button className="setup-skip-btn" onClick={() => navigate('/')}>
              Überspringen – später einrichten
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
